import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { accessTokens } from 'api/accessTokenMgmt'
import { MEMES_QUERY_POLICY } from 'api/queryPolicies'
import { getMemesQueryKey, MemesSort, MemeTopWindow } from 'lib/memeSort'
import { linkedFeedQueryKey } from 'lib/feedPagination'

export interface MemeParticipant {
    id: string
    name?: string | null
    avatar_urls?: {
        original?: string
        small?: string
        medium?: string
        large?: string
    } | null
}

export interface MemePreviewAnswer {
    id: string
    user?: MemeParticipant | null
    created_at?: string | null
    content?: string | null
    delta_json?: unknown
    nested_delta_json?: unknown
    bsv_upvalue_amt?: number | null
    embeds?: Array<{ id?: unknown } | null | undefined>
    [key: string]: unknown
}

export interface MemePreviewMedia {
    kind: 'image' | 'video'
    url: string
    poster_url?: string | null
    duration?: string | null
    width?: number | null
    height?: number | null
}

export interface MemeWinningAnswer extends MemePreviewAnswer {
    upvalue_total_sats?: number | null
    media?: MemePreviewMedia | null
}

export interface MemeTopThread {
    id: string
    space_id?: string | null
    path?: string | null
    parent?: MemePreviewAnswer | null
    winning_answer?: MemeWinningAnswer | null
    reply_count?: number
    upvalue_total_sats?: number | null
    [key: string]: unknown
}

export interface MemeHub {
    id: string
    text: string
    path: string
    thread_count: number
    usage_count?: number
    recent_thread_count?: number | null
    last_activity_at?: string | null
    // Present only when the panel requests embed=true. top_thread is a hydrated
    // quest JSON whose `winning_answer` is the meme-carrying post to preview.
    // `parent` remains available as a fallback for stale or partial data.
    upvalue_total_sats?: number | null
    top_new_unseen?: boolean
    participant_count?: number
    participants?: MemeParticipant[]
    top_thread?: MemeTopThread | null
}

interface GetMemesResponse {
    memes: MemeHub[]
    has_more: boolean
    next_page?: number
    total_count: number
}

const fetchMemes = async (
    sort: MemesSort,
    pageParam: number,
    embed: boolean,
    scope?: 'mine',
    topWindow?: MemeTopWindow,
): Promise<GetMemesResponse> => {
    const embedParam = embed ? '&embed=true' : ''
    const scopeParam = scope ? `&scope=${scope}` : ''
    const topWindowParam = topWindow ? `&top_window=${topWindow}` : ''
    const response = await fetch(
        `${window.knovApiUrl}/api/v1/memes?sort=${sort}&page=${pageParam}${embedParam}${scopeParam}${topWindowParam}`,
        {
            headers: {
                ...api.commonHeaders(),
                ...accessTokens(),
                'X-CSRF-Token': gon.formToken,
            },
            credentials: 'same-origin',
        },
    )

    if (!response.ok) {
        throw new Error('Failed to fetch memes')
    }

    const data = await response.json()
    return {
        memes: data.memes || [],
        has_more: data.has_more || false,
        next_page: data.next_page,
        total_count: data.total_count || 0,
    }
}

export const useGetMemes = (
    sort: MemesSort,
    {
        enabled = true,
        embed = false,
        scope,
        topWindow,
        initialPage = 1,
    }: {
        enabled?: boolean
        embed?: boolean
        scope?: 'mine'
        topWindow?: MemeTopWindow
        // Hard loads of /memes?page=N (crawlers, deep links) start the feed at
        // that batch, making the linked pagination chain real.
        initialPage?: number
    } = {},
) => {
    const query = useInfiniteQuery({
        queryKey: linkedFeedQueryKey(getMemesQueryKey(sort, topWindow, embed, scope), initialPage),
        queryFn: ({ pageParam = 1 }) => fetchMemes(sort, pageParam, embed, scope, topWindow),
        enabled,
        staleTime: MEMES_QUERY_POLICY.staleTime,
        gcTime: MEMES_QUERY_POLICY.gcTime,
        retry: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        structuralSharing: false,
        networkMode: 'always',
        initialPageParam: initialPage,
        getNextPageParam: lastPage => (lastPage.has_more ? lastPage.next_page : undefined),
    })

    // Flatten all pages into a single array of meme hubs
    const memes = query.data?.pages.flatMap(page => page.memes) || []

    // The next linked-pagination page (?page=N), when the server says more exist.
    const lastLoadedPage = query.data?.pages[query.data.pages.length - 1]
    const nextPage = lastLoadedPage?.has_more ? lastLoadedPage.next_page : undefined

    return {
        memes,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        nextPage,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
        refetch: query.refetch,
        totalMemes: query.data?.pages[0]?.total_count || 0,
    }
}
